// components/organisation/useOrganisationMemberActions.tsx
import React, { useCallback, useMemo, useState } from 'react';
import { AxiosError } from 'axios';
import { toast } from 'sonner';
import { Check, Copy, X } from '@/components/ui/lucide-memo';
import { useKernel } from '@/kernel/Kernel';
import DeleteUserModal from '@/components/DeleteUserModal';
import UserPermissionsModal from '@/components/UserPermissionsModal';
import { GenericEntitySelector } from '@/components/portal/GenericEntitySelector';
import type { MemberData, QuickMenuAction, UserDeletionOptions, UserPermissions } from '#types/types';
import { buildOrganisationMemberContextActions } from './memberContextActions';

interface UseOrganisationMemberActionsParams {
  organisationId?: string | null;
  refreshOrganisationId?: string | null;
  members: MemberData[];
  isFaterAdmin: boolean;
  canDelete: boolean;
  canManageSubordinates: boolean;
  isCurrentUser: (member: MemberData) => boolean;
  canChangeEmail?: (member: MemberData) => boolean;
  canDeleteMember?: (member: MemberData) => boolean;
  onAvatarMutation?: () => void;
}

interface UseOrganisationMemberActionsResult {
  avatarTimestamp: number;
  buildMemberContextActions: (member: MemberData) => QuickMenuAction[];
  openMemberContextMenu: (member: MemberData, event: React.MouseEvent, extraActions?: QuickMenuAction[]) => void;
  memberActionModals: React.ReactNode;
}

const getAxiosMessage = (error: unknown, fallback: string): string => {
  if (error instanceof AxiosError && error.response?.data) {
    const data = error.response.data;
    return typeof data === 'string' ? data : (data.message || JSON.stringify(data));
  }
  if (error instanceof Error && error.message) return error.message;
  return fallback;
};

export const useOrganisationMemberActions = ({
  organisationId,
  refreshOrganisationId,
  members,
  isFaterAdmin,
  canDelete,
  canManageSubordinates,
  isCurrentUser,
  canChangeEmail,
  canDeleteMember,
  onAvatarMutation,
}: UseOrganisationMemberActionsParams): UseOrganisationMemberActionsResult => {
  const { kernel } = useKernel();
  const effectiveRefreshOrganisationId = refreshOrganisationId === undefined ? organisationId ?? null : refreshOrganisationId;
  const [avatarTimestamp, setAvatarTimestamp] = useState(0);
  const [togglingAdminId, setTogglingAdminId] = useState<string | null>(null);
  const [changingEmailUserId, setChangingEmailUserId] = useState<string | null>(null);
  const [sendingTeamReportUserId, setSendingTeamReportUserId] = useState<string | null>(null);
  const [userToDelete, setUserToDelete] = useState<MemberData | null>(null);
  const [permissionsModalUser, setPermissionsModalUser] = useState<MemberData | null>(null);
  const [passwordDisplay, setPasswordDisplay] = useState<{ email: string; pass: string } | null>(null);
  const [copied, setCopied] = useState(false);
  const [subordinateTargetUser, setSubordinateTargetUser] = useState<MemberData | null>(null);
  const [selectedSubordinateIds, setSelectedSubordinateIds] = useState<string[]>([]);
  const [subordinateTargetUsageInput, setSubordinateTargetUsageInput] = useState('');
  const [subordinateTargetGroupNameInput, setSubordinateTargetGroupNameInput] = useState('');

  const markAvatarMutation = useCallback(() => {
    setAvatarTimestamp(Date.now());
    onAvatarMutation?.();
  }, [onAvatarMutation]);

  const refreshOrganisation = useCallback(async () => {
    await kernel.current?.refreshOrganisationDetails(effectiveRefreshOrganisationId ?? null, 'full');
  }, [effectiveRefreshOrganisationId, kernel]);

  const handleImpersonateUser = useCallback(async (userId: string, userName: string) => {
    const toastId = toast.loading(`Starting impersonation session for ${userName}...`);
    try {
      await kernel.current?.impersonateOrganisationMember(userId);
      toast.success(`Session started. Reloading as ${userName}...`, { id: toastId });
      window.location.reload();
    } catch (error) {
      toast.error(getAxiosMessage(error, 'Failed to start impersonation.'), { id: toastId });
    }
  }, [kernel]);

  const handleResetPassword = useCallback(async (member: MemberData) => {
    if (!kernel.current) return;
    if (!window.confirm(`Are you sure you want to reset the password for ${member.email}? This action cannot be undone.`)) return;

    const toastId = toast.loading(`Resetting password for ${member.email}...`);
    try {
      const { newPassword } = await kernel.current.resetOrganisationMemberPassword(member.id);
      toast.success(`Password for ${member.email} has been reset.`, { id: toastId });
      setPasswordDisplay({ email: member.email, pass: newPassword });
    } catch (error) {
      toast.error(getAxiosMessage(error, 'Failed to reset password.'), { id: toastId });
    }
  }, [kernel]);

  const handleToggleAdmin = useCallback(async (member: MemberData) => {
    if (!organisationId) return;

    const nextStatus = !member.isOrganisationAdmin;
    const actionText = nextStatus ? 'promote' : 'revoke admin from';
    if (!window.confirm(`Are you sure you want to ${actionText} ${member.firstName || member.email}?`)) return;

    const toastId = toast.loading(`${nextStatus ? 'Promoting' : 'Demoting'} user...`);
    setTogglingAdminId(member.id);
    try {
      await kernel.current?.setOrganisationMemberAdmin(organisationId, member.id, nextStatus);
      toast.success(`User successfully ${nextStatus ? 'promoted to Admin' : 'demoted to Member'}.`, { id: toastId });
    } catch (error) {
      toast.error(getAxiosMessage(error, 'Failed to change admin status.'), { id: toastId });
    } finally {
      setTogglingAdminId(null);
    }
  }, [kernel, organisationId]);

  const handleChangeMemberEmail = useCallback(async (member: MemberData) => {
    if (!organisationId || !kernel.current) return;
    if (!member.authId || member.authId.startsWith('invited_')) {
      toast.error('Cannot change email for invited users who have not signed up yet.');
      return;
    }

    const nextEmailInput = window.prompt(`New email address for ${member.firstName || member.email}:`, member.email);
    if (nextEmailInput === null) return;

    const toastId = toast.loading(`Updating email for ${member.email}...`);
    setChangingEmailUserId(member.id);
    try {
      const nextEmail = await kernel.current.changeOrganisationMemberEmail(organisationId, member.id, member.email, nextEmailInput);
      await refreshOrganisation();
      toast.success(`Email updated to ${nextEmail}.`, { id: toastId });
    } catch (error) {
      toast.error(getAxiosMessage(error, 'Failed to change email.'), { id: toastId });
    } finally {
      setChangingEmailUserId(null);
    }
  }, [kernel, organisationId, refreshOrganisation]);

  const handleDeleteUser = useCallback(async (userId: string, options: UserDeletionOptions) => {
    if (!organisationId) {
      throw new Error('Organisation context is missing.');
    }

    const member = members.find((candidate) => candidate.id === userId);
    const userEmail = member?.email || 'this user';
    const toastId = toast.loading(`Deleting user ${userEmail}...`);
    try {
      await kernel.current?.deleteOrganisationMember(organisationId, userId, options);
      await refreshOrganisation();
      setUserToDelete(null);
      toast.success('User deletion submitted.', { id: toastId });
    } catch (error) {
      const message = getAxiosMessage(error, 'Failed to delete user.');
      toast.error(message, { id: toastId });
      throw new Error(message);
    }
  }, [kernel, members, organisationId, refreshOrganisation]);

  const handleDownloadAvatar = useCallback(async (member: MemberData) => {
    const toastId = toast.loading('Preparing download...');
    try {
      const response = await fetch(`/api/user/avatar?userId=${member.id}`);
      if (!response.ok) throw new Error('Image not found');
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Fater_Avatar_${member.firstName || member.email.split('@')[0]}.webp`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Download started', { id: toastId });
    } catch (error) {
      toast.error('Could not download avatar', { id: toastId });
    }
  }, []);

  const handleDeleteAvatar = useCallback(async (member: MemberData) => {
    if (!window.confirm(`Delete avatar for ${member.firstName || 'this user'}?`)) return;

    const toastId = toast.loading('Deleting avatar...');
    try {
      await kernel.current?.deleteOrganisationMemberAvatar(member.id);
      markAvatarMutation();
      toast.success('Avatar deleted', { id: toastId });
    } catch (error) {
      toast.error(getAxiosMessage(error, 'Failed to delete avatar'), { id: toastId });
    }
  }, [kernel, markAvatarMutation]);

  const handleOpenSubordinateSelector = useCallback((member: MemberData) => {
    const initialIds = Array.isArray(member.subordinateIds)
      ? member.subordinateIds.filter((id) => id !== member.id)
      : [];
    const latestTargetEntry = Array.isArray(member.groupUsageTargets)
      ? [...member.groupUsageTargets]
        .sort((a, b) => new Date(a.addedAt).getTime() - new Date(b.addedAt).getTime())
        .at(-1) ?? null
      : null;
    setSubordinateTargetUser(member);
    setSelectedSubordinateIds(Array.from(new Set(initialIds)));
    setSubordinateTargetUsageInput(
      latestTargetEntry && Number.isFinite(latestTargetEntry.value) ? String(latestTargetEntry.value) : ''
    );
    setSubordinateTargetGroupNameInput(latestTargetEntry?.groupName?.trim() ?? '');
  }, []);

  const handleCloseSubordinateSelector = useCallback(() => {
    setSubordinateTargetUser(null);
    setSelectedSubordinateIds([]);
    setSubordinateTargetUsageInput('');
    setSubordinateTargetGroupNameInput('');
  }, []);

  const handleToggleSubordinateSelection = useCallback((userId: string) => {
    setSelectedSubordinateIds((previous) => (
      previous.includes(userId)
        ? previous.filter((id) => id !== userId)
        : [...previous, userId]
    ));
  }, []);

  const handleConfirmSubordinateSelection = useCallback(async () => {
    if (!organisationId || !subordinateTargetUser) {
      throw new Error('Organisation or manager context is missing.');
    }

    try {
      await kernel.current?.setOrganisationMemberSubordinates({
        organisationId,
        managerUserId: subordinateTargetUser.id,
        subordinateUserIds: selectedSubordinateIds,
        targetWeeklyUsageCredits: subordinateTargetUsageInput,
        targetGroupName: subordinateTargetGroupNameInput,
      });
      await refreshOrganisation();
      toast.success('Subordinates updated.');
      handleCloseSubordinateSelector();
    } catch (error) {
      toast.error(getAxiosMessage(error, 'Failed to update subordinates.'));
      throw error;
    }
  }, [handleCloseSubordinateSelector, kernel, organisationId, refreshOrganisation, selectedSubordinateIds, subordinateTargetGroupNameInput, subordinateTargetUsageInput, subordinateTargetUser]);

  const handleSendTeamReportToSelf = useCallback(async (member: MemberData) => {
    if (!kernel.current) {
      toast.error('Kernel unavailable.');
      return;
    }

    const toastId = toast.loading('Sending team report to your inbox...');
    setSendingTeamReportUserId(member.id);
    try {
      const response = await kernel.current.sendUserTeamReport(member.id);
      toast.success(`Team report sent to ${response?.recipientEmail || 'your inbox'}.`, { id: toastId });
    } catch (error) {
      toast.error(getAxiosMessage(error, 'Failed to send team report.'), { id: toastId });
    } finally {
      setSendingTeamReportUserId(null);
    }
  }, [kernel]);

  const handleSavePermissions = useCallback(async (userId: string, permissions: UserPermissions) => {
    const toastId = toast.loading('Updating permissions...');
    try {
      await kernel.current?.setOrganisationMemberPermissions(userId, permissions);
      toast.success('Permissions updated.', { id: toastId });
    } catch (error) {
      toast.error(getAxiosMessage(error, 'Failed to update permissions.'), { id: toastId });
      throw error;
    }
  }, [kernel]);

  const subordinateSelectorAllowedIds = useMemo(() => {
    if (!subordinateTargetUser) return [];
    return members
      .map((member) => member.id)
      .filter((id) => id !== subordinateTargetUser.id);
  }, [members, subordinateTargetUser]);

  const subordinateSelectorFilters = useMemo(() => {
    if (!subordinateTargetUser) return undefined;
    return {
      organisationId: organisationId || undefined,
      ids: subordinateSelectorAllowedIds,
    };
  }, [organisationId, subordinateSelectorAllowedIds, subordinateTargetUser]);

  const buildMemberContextActions = useCallback((member: MemberData): QuickMenuAction[] => {
    return buildOrganisationMemberContextActions(member, {
      currentOrgId: organisationId,
      isFaterAdmin,
      canDelete,
      canManageSubordinates,
      canChangeEmail: canChangeEmail ?? ((targetMember) => canDelete && !isCurrentUser(targetMember) && (!targetMember.isOrganisationAdmin || isFaterAdmin)),
      canDeleteMember: canDeleteMember ?? ((targetMember) => canDelete && !targetMember.isOrganisationAdmin),
      isCurrentUser,
      togglingAdminId,
      changingEmailUserId,
      sendingTeamReportUserId,
      onImpersonateUser: handleImpersonateUser,
      onResetPassword: handleResetPassword,
      onEditPermissions: setPermissionsModalUser,
      onDownloadAvatar: handleDownloadAvatar,
      onDeleteAvatar: handleDeleteAvatar,
      onOpenSubordinateSelector: handleOpenSubordinateSelector,
      onSendTeamReportToSelf: handleSendTeamReportToSelf,
      onToggleAdmin: handleToggleAdmin,
      onChangeMemberEmail: handleChangeMemberEmail,
      onDeleteUser: setUserToDelete,
    });
  }, [canChangeEmail, canDelete, canDeleteMember, canManageSubordinates, changingEmailUserId, handleChangeMemberEmail, handleDeleteAvatar, handleDownloadAvatar, handleImpersonateUser, handleOpenSubordinateSelector, handleResetPassword, handleSendTeamReportToSelf, handleToggleAdmin, isCurrentUser, isFaterAdmin, organisationId, sendingTeamReportUserId, togglingAdminId]);

  const openMemberContextMenu = useCallback((
    member: MemberData,
    event: React.MouseEvent,
    extraActions: QuickMenuAction[] = [],
  ) => {
    const actions = [...buildMemberContextActions(member), ...extraActions];
    if (actions.length === 0) return;
    event.preventDefault();
    event.stopPropagation();
    kernel.current?.setMenuData({ position: { x: event.clientX, y: event.clientY }, actions });
  }, [buildMemberContextActions, kernel]);

  const memberActionModals = useMemo(() => (
    <>
      {userToDelete && (
        <DeleteUserModal
          isOpen={Boolean(userToDelete)}
          onClose={() => setUserToDelete(null)}
          userId={userToDelete.id}
          userEmail={userToDelete.email}
          onSubmit={handleDeleteUser}
        />
      )}

      {permissionsModalUser && (
        <UserPermissionsModal
          key={permissionsModalUser.id}
          isOpen={Boolean(permissionsModalUser)}
          onClose={() => setPermissionsModalUser(null)}
          userId={permissionsModalUser.id}
          onSubmit={handleSavePermissions}
        />
      )}

      {passwordDisplay && (
        <div className="fixed inset-0 z-[101] flex items-center justify-center bg-[#141414]/90 p-4 backdrop-blur-sm" onClick={() => { setPasswordDisplay(null); setCopied(false); }}>
          <div
            className="relative w-full max-w-md overflow-hidden rounded-[24px] border border-white/[0.06] bg-[#212121] text-white shadow-2xl"
            onClick={(event) => event.stopPropagation()}
          >
            <button
              type="button"
              onClick={() => { setPasswordDisplay(null); setCopied(false); }}
              className="absolute right-3 top-3 rounded-md p-1.5 text-white/35 transition-colors hover:bg-white/[0.06] hover:text-white/80"
              aria-label="Close modal"
            >
              <X size={17} />
            </button>

            <div className="p-5">
              <h2 className="text-lg font-semibold text-white/90">Password reset</h2>
              <p className="mt-1 text-sm text-white/45">Copy these credentials and share them securely with the user.</p>
              <div className="relative mt-4 rounded-lg border border-[#2A2A2A] bg-[#141414] p-3 pr-11 text-left">
                <pre className="whitespace-pre-wrap break-all font-mono text-xs leading-5 text-white/70">
                  {`Email: ${passwordDisplay.email}\nNew password: ${passwordDisplay.pass}`}
                </pre>
                <button
                  type="button"
                  onClick={() => {
                    navigator.clipboard.writeText(`Email: ${passwordDisplay.email}\nNew password: ${passwordDisplay.pass}`);
                    setCopied(true);
                    setTimeout(() => setCopied(false), 2000);
                  }}
                  title="Copy credentials"
                  className="absolute right-2 top-2 rounded-md bg-white/[0.04] p-1.5 text-white/45 transition-colors hover:bg-white/[0.08] hover:text-white/80"
                >
                  {copied ? <Check size={15} className="text-[#4DAB9A]" /> : <Copy size={15} />}
                </button>
              </div>
              <div className="mt-5 flex justify-end">
                <button
                  type="button"
                  onClick={() => { setPasswordDisplay(null); setCopied(false); }}
                  className="inline-flex h-8 items-center justify-center rounded-md bg-white px-3 text-sm font-medium text-black transition-opacity hover:opacity-90"
                >
                  Done
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {subordinateTargetUser && (
        <GenericEntitySelector
          type="User"
          selectedIds={selectedSubordinateIds}
          onSelect={(id) => {
            if (id === subordinateTargetUser.id) return;
            handleToggleSubordinateSelection(id);
          }}
          isMultiSelect
          filters={subordinateSelectorFilters}
          title={`Add subordinates - ${subordinateTargetUser.firstName || subordinateTargetUser.email}`}
          isOpen={Boolean(subordinateTargetUser)}
          onOpenChange={(open) => {
            if (!open) handleCloseSubordinateSelector();
          }}
          onConfirmSelection={handleConfirmSubordinateSelection}
          multiSelectFooterExtra={
            <div className="flex flex-col gap-1.5">
              <div className="flex flex-wrap items-end gap-3">
                <div className="flex flex-col gap-1.5">
                  <label className="text-xs font-medium text-white/55">Target usage per month</label>
                  <input
                    type="number"
                    min={0}
                    step="1"
                    value={subordinateTargetUsageInput}
                    onChange={(event) => setSubordinateTargetUsageInput(event.target.value)}
                    placeholder="Optional"
                    className="h-8 w-56 rounded-full border border-[#2A2A2A] bg-transparent px-2.5 text-[13px] text-white/85 outline-none placeholder:text-white/25 focus:border-white/20"
                  />
                </div>
                <div className="flex flex-col gap-1.5">
                  <label className="text-xs font-medium text-white/55">Group name</label>
                  <input
                    type="text"
                    maxLength={80}
                    value={subordinateTargetGroupNameInput}
                    onChange={(event) => setSubordinateTargetGroupNameInput(event.target.value)}
                    placeholder="Optional"
                    className="h-8 w-56 rounded-full border border-[#2A2A2A] bg-transparent px-2.5 text-[13px] text-white/85 outline-none placeholder:text-white/25 focus:border-white/20"
                  />
                </div>
              </div>
              <p className="text-[11px] text-white/35">
                Optional. When set, this adds a dated target entry and group name for reporting.
              </p>
            </div>
          }
          renderTrigger={() => null}
          variant="row"
        />
      )}
    </>
  ), [copied, handleCloseSubordinateSelector, handleConfirmSubordinateSelection, handleDeleteUser, handleSavePermissions, handleToggleSubordinateSelection, passwordDisplay, permissionsModalUser, selectedSubordinateIds, subordinateSelectorFilters, subordinateTargetGroupNameInput, subordinateTargetUsageInput, subordinateTargetUser, userToDelete]);

  return {
    avatarTimestamp,
    buildMemberContextActions,
    openMemberContextMenu,
    memberActionModals,
  };
};
